// MetricsRow.jsx

import React, { useState, useMemo } from 'react';
import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import Modal from 'react-modal';
import Tooltip from './Tooltip';
import '../css/MetricsRow.css';

// Pretvori sate i minute u decimalne sate (60 min = 1h)
const toDecimalHours = (hours, minutes) => {
  const h = parseInt(hours, 10) || 0;
  const m = Math.min(59, Math.max(0, parseInt(minutes, 10) || 0));
  return h + m / 60;
};

// Iz decimalnih sati izvuci sate i minute
const fromDecimalHours = (decimal) => {
  if (decimal == null || isNaN(decimal) || decimal <= 0) return { hours: 0, minutes: 0 };
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return { hours: h, minutes: m };
};

// Format za prikaz (npr. "9h 0min")
const formatDuration = (decimalHours) => {
  const { hours, minutes } = fromDecimalHours(decimalHours);
  if (hours === 0 && minutes === 0) return '0h';
  return `${hours}h ${minutes}min`;
};

function MetricsRow({
  person,
  dailyMetricsData = [],
  selectedYear,
  selectedMonth,
  refreshData,
  firms = [],
  metrickePostavke = {},
  daysInMonth = 35,
  isPinned,
  onTogglePin,
  hiddenColumns = {},
  highlightedDays = [],
}) {
  // STARO: Modal za unos zarade (amount) - tablica zarada
  // const [isModalOpen, setIsModalOpen] = useState(false);
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [currentDay, setCurrentDay] = useState(null);
  // const [dailyAmount, setDailyAmount] = useState(0);
  const [workDetails, setWorkDetails] = useState({
    glovoHours: 0,
    glovoMinutes: 0,
    woltHours: 0,
    woltMinutes: 0,
  });
  const [isRowSelected, setIsRowSelected] = useState(false);

  /* STARO: Modal za unos zarade (amount) - openModal, closeModal, handleSave, handleDelete
  const openModal = (day) => {
    const metric = dailyMetricsData.find((m) => m.day === day);
    setCurrentDay(day);
    setDailyAmount(metric ? parseFloat(metric.amount) : 0);
    setIsModalOpen(true);
  };
  const closeModal = () => { setIsModalOpen(false); setCurrentDay(null); setDailyAmount(0); };
  const handleSave = async () => {
    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(metricsCollection, where('userId', '==', person.id), where('year', '==', selectedYear), where('month', '==', selectedMonth), where('day', '==', currentDay));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, { amount: dailyAmount });
      } else {
        await addDoc(metricsCollection, { userId: person.id, year: selectedYear, month: selectedMonth, day: currentDay, amount: dailyAmount });
      }
      alert('Metrika uspješno spremljena!');
      closeModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri spremanju metrike:', e);
      alert('Došlo je do greške prilikom spremanja metrike. Pokušajte ponovno.');
    }
  };
  const handleDelete = async () => {
    if (!window.confirm(`Jeste li sigurni da želite obrisati metriku za dan ${currentDay}?`)) return;
    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(metricsCollection, where('userId', '==', person.id), where('year', '==', selectedYear), where('month', '==', selectedMonth), where('day', '==', currentDay));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await deleteDoc(docRef);
        alert('Metrika uspješno izbrisana!');
        closeModal();
        refreshData();
      }
    } catch (e) {
      console.error('Greška pri brisanju metrike:', e);
      alert('Došlo je do greške prilikom brisanja metrike. Pokušajte ponovno.');
    }
  };
  */

  const openWorkModal = (day) => {
    const metric = dailyMetricsData.find((m) => m.day === day);
    setCurrentDay(day);
    const glovoH = metric?.glovoHours ?? (metric?.actualWorkingHours ? parseFloat(metric.actualWorkingHours) : 0);
    const woltH = metric?.woltHours ?? (metric?.plannedWorkingHours ? parseFloat(metric.plannedWorkingHours) : 0);
    const glovo = fromDecimalHours(glovoH);
    const wolt = fromDecimalHours(woltH);
    setWorkDetails({
      glovoHours: glovo.hours,
      glovoMinutes: glovo.minutes,
      woltHours: wolt.hours,
      woltMinutes: wolt.minutes,
    });
    setWorkModalOpen(true);
  };

  const closeWorkModal = () => {
    setWorkModalOpen(false);
    setCurrentDay(null);
    setWorkDetails({ glovoHours: 0, glovoMinutes: 0, woltHours: 0, woltMinutes: 0 });
  };

  const handleSaveWorkDetails = async () => {
    const glovoH = toDecimalHours(workDetails.glovoHours, workDetails.glovoMinutes);
    const woltH = toDecimalHours(workDetails.woltHours, workDetails.woltMinutes);

    try {
      const metricsCollection = collection(db, 'dailyMetrics');
      const q = query(
        metricsCollection,
        where('userId', '==', person.id),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth),
        where('day', '==', currentDay)
      );
      const querySnapshot = await getDocs(q);

      const updateData = { glovoHours: glovoH, woltHours: woltH };

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, updateData);
      } else {
        await addDoc(metricsCollection, {
          userId: person.id,
          year: selectedYear,
          month: selectedMonth,
          day: currentDay,
          amount: 0,
          ...updateData,
        });
      }

      alert('Glovo/Wolt sati uspješno spremljeni!');
      closeWorkModal();
      refreshData();
    } catch (e) {
      console.error('Greška pri spremanju sati:', e);
      alert('Došlo je do greške prilikom spremanja sati. Pokušajte ponovno.');
    }
  };

  const getFirmName = (firmId) => {
    const firm = firms.find((f) => f.id === firmId);
    return firm ? firm.name : 'N/A';
  };

  // STARO: Boja ćelije prema minimalnoj dnevnoj zaradi (low-earning/high-earning)
  // const getCellClass = (amount) => {
  //   if (person.nationality === 'Stranac' && amount < metrickePostavke.minDailyStranci) return 'low-earning';
  //   if (person.nationality === 'Hrvat' && amount < metrickePostavke.minDailyHrvati) return 'low-earning';
  //   return '';
  // };

  // STARO: Ukupna mjesečna zarada - za prikaz u stupcu Suma (tablica zarada)
  // const monthlySum = useMemo(() => {
  //   return dailyMetricsData.reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
  // }, [dailyMetricsData]);

  const totalGlovoHours = useMemo(() => {
    return dailyMetricsData.reduce((sum, m) => {
      const h = m.glovoHours ?? (m.actualWorkingHours ? parseFloat(m.actualWorkingHours) : 0);
      return sum + (parseFloat(h) || 0);
    }, 0);
  }, [dailyMetricsData]);

  const totalWoltHours = useMemo(() => {
    return dailyMetricsData.reduce((sum, m) => {
      const h = m.woltHours ?? (m.plannedWorkingHours ? parseFloat(m.plannedWorkingHours) : 0);
      return sum + (parseFloat(h) || 0);
    }, 0);
  }, [dailyMetricsData]);

  const totalActualWorkingHours = totalGlovoHours + totalWoltHours;

  // STARO: Broj dana s unesenom zaradom - za Dnevni prosjek
  // const daysWorked = useMemo(() => {
  //   return dailyMetricsData.filter((m) => m.amount !== undefined && m.amount !== null).length;
  // }, [dailyMetricsData]);
  // const averageEarnings = daysWorked > 0 ? monthlySum / daysWorked : 0;

  // STARO: Boja stupca Suma prema minimalnoj mjesečnoj zaradi
  // const getSumCellClass = () => {
  //   if (person.nationality === 'Stranac') {
  //     if (monthlySum >= metrickePostavke.minMonthlyStranci) return 'high-earning';
  //     if (monthlySum < metrickePostavke.minMonthlyStranci) return 'low-earning';
  //   }
  //   if (person.nationality === 'Hrvat') {
  //     if (monthlySum >= metrickePostavke.minMonthlyHrvati) return 'high-earning';
  //     if (monthlySum < metrickePostavke.minMonthlyHrvati) return 'low-earning';
  //   }
  //   return '';
  // };

  const getMetricForDay = (day) => {
    const m = dailyMetricsData.find((d) => d.day === day);
    if (!m) return null;
    const glovoH = m.glovoHours ?? (m.actualWorkingHours ? parseFloat(m.actualWorkingHours) : 0);
    const woltH = m.woltHours ?? (m.plannedWorkingHours ? parseFloat(m.plannedWorkingHours) : 0);
    return {
      amount: parseFloat(m.amount) || 0,
      glovoHours: parseFloat(glovoH) || 0,
      woltHours: parseFloat(woltH) || 0,
      totalHours: (parseFloat(glovoH) || 0) + (parseFloat(woltH) || 0),
    };
  };

  // Zbroj sati za tjedan (tjedan 1 = dani 1-7, tjedan 2 = dani 8-14, itd.)
  const getWeekTotals = (weekIndex) => {
    const startDay = weekIndex * 7 + 1;
    const endDay = Math.min(startDay + 6, daysInMonth);
    let glovo = 0;
    let wolt = 0;
    let total = 0;
    for (let d = startDay; d <= endDay; d++) {
      const data = getMetricForDay(d);
      if (data) {
        glovo += data.glovoHours;
        wolt += data.woltHours;
        total += data.totalHours;
      }
    }
    return { glovo, wolt, total };
  };

  const cellClass = (isPinned ? 'selected-cell' : '') + (isRowSelected ? ' selected-cell' : '');
  const baseCellClass = `${cellClass}`.trim();

  const renderDayCells = (rowType) => {
    const cells = [];
    const numWeeks = Math.ceil(daysInMonth / 7);

    for (let week = 0; week < numWeeks; week++) {
      // Dani u tjednu
      for (let d = 1; d <= 7; d++) {
        const day = week * 7 + d;
        if (day > daysInMonth) break;

        const data = getMetricForDay(day);
        // STARO: amount i cellClassForAmount za prikaz zarade u zagradi i boju ćelije
        // const amount = data ? data.amount : 0;
        // const cellClassForAmount = data ? getCellClass(amount) : '';

        if (rowType === 'glovo') {
          const glovoH = data ? data.glovoHours : 0;
          const totalH = data ? data.totalHours : 0;
          cells.push(
            <td
              key={`day-${day}`}
              className={`${baseCellClass} ${highlightedDays.includes(day) ? 'day-highlighted' : ''}`}
              onClick={() => openWorkModal(day)}
              title={`Glovo: ${formatDuration(glovoH)} | Ukupno: ${formatDuration(totalH)}`}
            >
              <b>{glovoH > 0 ? formatDuration(glovoH) : '0h'}</b>
            </td>
          );
        } else if (rowType === 'wolt') {
          const woltH = data ? data.woltHours : 0;
          cells.push(
            <td key={`day-${day}`} className={`${baseCellClass} ${highlightedDays.includes(day) ? 'day-highlighted' : ''}`} onClick={() => openWorkModal(day)} title={`Wolt sati: ${formatDuration(woltH)}`}>
              <b>{woltH > 0 ? formatDuration(woltH) : '0h'}</b>
            </td>
          );
        } else if (rowType === 'ukupno') {
          const totalH = data ? data.totalHours : 0;
          cells.push(
            <td key={`day-${day}`} className={`${baseCellClass} ${highlightedDays.includes(day) ? 'day-highlighted' : ''}`} onClick={() => openWorkModal(day)} title={`Ukupno sati: ${formatDuration(totalH)}`}>
              <strong>{totalH > 0 ? formatDuration(totalH) : '0h'}</strong>
            </td>
          );
        }
      }

      // Stupac Tjedan ukupno nakon svakih 7 dana
      const weekTotals = getWeekTotals(week);
      const weekKey = `week-${week}`;
      if (rowType === 'glovo') {
        cells.push(
          <td key={weekKey} className={`${baseCellClass} week-total-cell`}>
            <strong>{formatDuration(weekTotals.glovo)}</strong>
          </td>
        );
      } else if (rowType === 'wolt') {
        cells.push(
          <td key={weekKey} className={`${baseCellClass} week-total-cell`}>
            <strong>{formatDuration(weekTotals.wolt)}</strong>
          </td>
        );
      } else if (rowType === 'ukupno') {
        cells.push(
          <td key={weekKey} className={`${baseCellClass} week-total-cell`}>
            <strong>{formatDuration(weekTotals.total)}</strong>
          </td>
        );
      }
    }
    return cells;
  };

  return (
    <>
      {/* Red 1: Glovo sati */}
      <tr>
        <td className={baseCellClass} onClick={() => setIsRowSelected(!isRowSelected)}>
          <Tooltip content={person.comment || ''}>{person.name}</Tooltip>
        </td>
        <td rowSpan={3} className={baseCellClass}>
          <input
            type="checkbox"
            checked={isPinned}
            onChange={() => onTogglePin(person.id)}
            title="Pinaj osobu na vrh tablice"
          />
        </td>
        <td rowSpan={3} className={`${baseCellClass} ${hiddenColumns.riderID ? 'hidden-column' : ''}`}>{person.riderID}</td>
        <td rowSpan={3} className={`${baseCellClass} ${hiddenColumns.grad ? 'hidden-column' : ''}`}>{person.city || 'N/A'}</td>
        <td rowSpan={3} className={`${baseCellClass} ${hiddenColumns.firma ? 'hidden-column' : ''}`}>{getFirmName(person.firmId)}</td>
        <td rowSpan={3} className={`${baseCellClass} ${hiddenColumns.nationality ? 'hidden-column' : ''}`}>{person.nationality === 'Hrvat' ? 'H' : 'S'}</td>
        {/* STARO: Stupac Suma (ukupna mjesečna zarada) - tablica zarada */}
        {/* <td rowSpan={3} className={`${baseCellClass} ${getSumCellClass()} ${hiddenColumns.suma ? 'hidden-column' : ''}`}>
          <strong>{monthlySum.toFixed(2)}</strong>
        </td> */}
        <td className={`${baseCellClass} ${hiddenColumns.sumaRadnihSati ? 'hidden-column' : ''}`}>
          <strong>{formatDuration(totalGlovoHours)}</strong>
        </td>
        {/* STARO: Stupac Dnevni prosjek (prosječna zarada po danu) */}
        {/* <td rowSpan={3} className={`${baseCellClass} ${hiddenColumns.dnevniProsjek ? 'hidden-column' : ''}`}>
          <strong>{averageEarnings.toFixed(2)}</strong>
        </td> */}
        {!hiddenColumns.dani && renderDayCells('glovo')}
      </tr>

      {/* Red 2: Wolt sati */}
      <tr>
        <td className={`${baseCellClass} sub-row-label`}>Wolt sati</td>
        <td className={`${baseCellClass} ${hiddenColumns.sumaRadnihSati ? 'hidden-column' : ''}`}>
          <strong>{formatDuration(totalWoltHours)}</strong>
        </td>
        {!hiddenColumns.dani && renderDayCells('wolt')}
      </tr>

      {/* Red 3: Ukupno sati */}
      <tr>
        <td className={`${baseCellClass} sub-row-label`}>Ukupno sati</td>
        <td className={`${baseCellClass} ${hiddenColumns.sumaRadnihSati ? 'hidden-column' : ''}`}>
          <strong>{formatDuration(totalActualWorkingHours)}</strong>
        </td>
        {!hiddenColumns.dani && renderDayCells('ukupno')}
      </tr>

      <Modal
        isOpen={workModalOpen}
        onRequestClose={closeWorkModal}
        contentLabel={`Glovo/Wolt sati za dan ${currentDay}`}
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Glovo / Wolt sati za dan {currentDay}</h2>
        <p className="duration-hint">Trajanje rada (količina vremena, 60 min = 1h)</p>
        <form className="edit-work-details-form">
          <div className="form-group duration-input-group">
            <label>Glovo sati:</label>
            <div className="duration-inputs">
              <input
                type="number"
                min="0"
                max="24"
                step="1"
                value={workDetails.glovoHours}
                onChange={(e) => setWorkDetails({ ...workDetails, glovoHours: Math.min(24, Math.max(0, parseInt(e.target.value) || 0)) })}
                placeholder="h"
              />
              <span>h</span>
              <input
                type="number"
                min="0"
                max="99"
                step="1"
                value={workDetails.glovoMinutes}
                onChange={(e) => {
                  let m = parseInt(e.target.value) || 0;
                  let h = workDetails.glovoHours;
                  if (m >= 60) {
                    h += Math.floor(m / 60);
                    m = m % 60;
                  }
                  setWorkDetails({ ...workDetails, glovoHours: Math.min(24, h), glovoMinutes: Math.min(59, Math.max(0, m)) });
                }}
                placeholder="min"
              />
              <span>min</span>
            </div>
          </div>
          <div className="form-group duration-input-group">
            <label>Wolt sati:</label>
            <div className="duration-inputs">
              <input
                type="number"
                min="0"
                max="24"
                step="1"
                value={workDetails.woltHours}
                onChange={(e) => setWorkDetails({ ...workDetails, woltHours: Math.min(24, Math.max(0, parseInt(e.target.value) || 0)) })}
                placeholder="h"
              />
              <span>h</span>
              <input
                type="number"
                min="0"
                max="99"
                step="1"
                value={workDetails.woltMinutes}
                onChange={(e) => {
                  let m = parseInt(e.target.value) || 0;
                  let h = workDetails.woltHours;
                  if (m >= 60) {
                    h += Math.floor(m / 60);
                    m = m % 60;
                  }
                  setWorkDetails({ ...workDetails, woltHours: Math.min(24, h), woltMinutes: Math.min(59, Math.max(0, m)) });
                }}
                placeholder="min"
              />
              <span>min</span>
            </div>
          </div>
          <div className="form-group total-preview">
            <strong>Ukupno: {formatDuration(toDecimalHours(workDetails.glovoHours, workDetails.glovoMinutes) + toDecimalHours(workDetails.woltHours, workDetails.woltMinutes))}</strong>
          </div>
          <div className="button-group">
            <button type="button" className="save-button" onClick={handleSaveWorkDetails}>
              Spremi
            </button>
            <button type="button" className="cancel-button" onClick={closeWorkModal}>
              Odustani
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default MetricsRow;
